import React, { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isToday } from 'date-fns';
import { ICartItem, ITypeVignettenCheckoutFields, VignetteContext, convertApiDate } from '@adac-mp/shared';
import { ContentLoader, ProductSummary, ProductSummaryVariants } from '../../index';
import { renderProductIcon } from '../../utils';

interface IProductSummaryContainerProps {
	cartItem: ICartItem;
	index: number;
	multipleProducts: boolean;
	openProductKey: string | undefined;
	showStackedView?: boolean;
	stackIsHovered?: boolean;
	hasCategories?: boolean;
	hasSpecialPlates?: boolean;
	hasFuelType?: boolean;
	memberNo?: string;
	handleToggle: (key: string) => void;
	setStackIsHovered?: React.Dispatch<React.SetStateAction<boolean>>;
	setShowStackedView?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProductSummaryContainer = ({
	cartItem,
	index,
	multipleProducts,
	openProductKey,
	showStackedView,
	stackIsHovered,
	hasCategories,
	hasSpecialPlates = false,
	hasFuelType,
	memberNo,
	handleToggle,
	setStackIsHovered,
	setShowStackedView,
}: IProductSummaryContainerProps) => {
	const location = useLocation();
	const { contentfulLabels } = useContext(VignetteContext);
	const { offer, systemOverlapping, checkInDateTime, checkOutDateTime } = cartItem;
	const [isLoading, setIsLoading] = useState(false);
	const isCheckoutPage = location.pathname.includes('checkout');
	const oneDayVignetteValidityHint =
		checkOutDateTime.date === checkInDateTime.date &&
		isToday(convertApiDate(checkOutDateTime)) &&
		new Date().getHours() >= 20;
	const productIcon = renderProductIcon({
		supplierId: offer?.supplierId,
		moduleTypeId: offer?.moduleTypeId,
		tollClass: offer.tollClass,
	});

	const currentLabels = contentfulLabels as ITypeVignettenCheckoutFields | undefined;

	return (
		<>
			<ContentLoader isLoading={isLoading} />
			<ProductSummary
				key={`${offer.serviceCatalogId}-${index}`}
				style={
					showStackedView
						? {
								zIndex: 5 - index,
								transform:
									index > 0
										? `scaleX(${100 - index * 3}%) translateY(-${105 + (index - 1) * 105}%)`
										: 'none',
						  }
						: {}
				}
				stacked={showStackedView}
				stackIsHovered={stackIsHovered}
				offer={offer}
				oneDayVignetteValidityHint={oneDayVignetteValidityHint}
				isCheckoutPage={isCheckoutPage}
				systemOverlapping={systemOverlapping}
				multipleProducts={multipleProducts}
				showProductPrice={multipleProducts}
				icon={productIcon}
				title={offer?.serviceCatalogName}
				cartLabels={currentLabels?.cart}
				category={
					offer && hasCategories
						? {
								moduleDetail: offer.moduleDetail,
								moduleDetailId: offer.moduleDetailId,
						  }
						: undefined
				}
				openProductKeyIndex={index}
				cardIsOpen={openProductKey === `${offer.serviceCatalogId}-${index}`}
				setStackIsHovered={setStackIsHovered}
				handleToggle={
					showStackedView && setShowStackedView
						? () => setShowStackedView((prev: boolean) => !prev)
						: handleToggle
				}>
				<ProductSummaryVariants
					cartLabels={currentLabels?.cart}
					multipleProducts={multipleProducts}
					cartItem={cartItem}
					hasCategories={hasCategories}
					hasSpecialPlates={hasSpecialPlates}
					hasFuelType={hasFuelType}
					memberNo={memberNo}
					setIsLoading={setIsLoading}
				/>
			</ProductSummary>
		</>
	);
};
